import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * @typedef {Array<string | number | null>} HcCardMediaEntry
 *   [url @width=1200, width, height, alt, pairMediaId | '', mediaType, focalPoint | '']
 * @typedef {{ sizes?: string, media?: Record<string, HcCardMediaEntry> }} HcCardMediaManifest
 */

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
/*
 * Swatch sync is batched across every card on the page: all measurements
 * first, then all attribute writes. A facet change morphs 50 cards in ONE
 * task, and at the microtask checkpoint 50 observers would otherwise fire
 * read-write-read-write, forcing a full layout per card in the frame the
 * shopper is already waiting on. Reading everything before writing anything
 * collapses that to a single layout. The flush is a microtask, so it still
 * lands in the same frame before paint - which is what keeps a morphed-in
 * strip from flashing both arrows.
 */
const hcSwatchQueue = new Set();

/*
 * ONE page-wide heal loop instead of an interval per card: 19 carousels
 * used to mean 19 timers firing every 900ms. Cards register while they
 * have a scroll strip; the loop skips entirely in hidden tabs, where
 * there is nobody to heal for.
 */
const hcHealCards = new Set();
let hcHealLoop = 0;

/*
 * Phantom-pointerenter guard - the ROOT CAUSE of cards stuck on their hover
 * pair (homepage sale carousel, cold-cache loads): pointerenter also fires
 * when content scrolls or reflows UNDER a stationary cursor, and during
 * lazy-image loading the page shifts cards beneath the parked pointer while
 * the matching pointerleave gets dropped in the same churn. A real hover is
 * always accompanied by pointer MOVEMENT within milliseconds; a phantom
 * enter arrives with a stale move timestamp. Honor enters only near genuine
 * movement. (The gallery self-heal stays as the backstop for any orphan
 * this cannot see - bfcache edges, dropped leaves mid-hover.)
 */
let hcLastPointerMoveTs = 0;
/** Last known MOUSE position, or null before the mouse has ever moved. */
let hcLastMoveX = null;
let hcLastMoveY = null;
document.addEventListener(
  'pointermove',
  (event) => {
    // MOUSE only. Every consumer of this already ignores non-mouse pointers,
    // and on a hybrid device (touchscreen laptop, pen) an unfiltered stamp
    // would let a touch-scroll vouch for a phantom MOUSE pointerenter fired
    // by that same scroll - reopening this exact bug on those devices.
    if (event.pointerType !== 'mouse') return;
    hcLastPointerMoveTs = Date.now();
    hcLastMoveX = event.clientX;
    hcLastMoveY = event.clientY;
  },
  { passive: true, capture: true }
);

/**
 * Did the POINTER move onto this element, or did the ELEMENT move under a
 * parked pointer? Compare the enter's coordinates with the last place the
 * mouse actually moved to:
 *
 *   real hover    the pointer travelled, so the crossing is reported at a
 *                 position the last pointermove had not reached yet -> differs
 *   phantom enter the pointer never moved; the page scrolled or reflowed
 *                 beneath it, so the browser reports the enter at exactly the
 *                 coordinates the mouse already occupied -> identical
 *
 * Position, not elapsed time: a time window has a cliff, and it wrongly
 * vouches for a phantom enter that lands moments after a genuine movement
 * elsewhere (mouse a moment ago, wheel-scroll now) - which is the residual
 * case a purely time-based gate let through.
 * Before the mouse has moved at all there is no hover to honour.
 * @param {PointerEvent} [event]
 */
function hcPointerActuallyMoved(event) {
  if (hcLastMoveX === null) return false;
  if (!event || typeof event.clientX !== 'number') {
    return Date.now() - hcLastPointerMoveTs < 200;
  }
  return event.clientX !== hcLastMoveX || event.clientY !== hcLastMoveY;
}

function hcSetHealRegistration(card, needed) {
  if (needed) hcHealCards.add(card);
  else hcHealCards.delete(card);
  if (hcHealCards.size && !hcHealLoop) {
    hcHealLoop = window.setInterval(() => {
      if (document.hidden) return;
      for (const c of hcHealCards) {
        if (c.isConnected) {
          hcScheduleSwatchSync(c);
          c.hcHealGallery?.();
        } else hcHealCards.delete(c);
      }
    }, 1200);
  } else if (!hcHealCards.size && hcHealLoop) {
    clearInterval(hcHealLoop);
    hcHealLoop = 0;
  }
}
let hcSwatchFlushQueued = false;

function hcScheduleSwatchSync(card) {
  hcSwatchQueue.add(card);
  if (hcSwatchFlushQueued) return;
  hcSwatchFlushQueued = true;
  queueMicrotask(() => {
    hcSwatchFlushQueued = false;
    const cards = [...hcSwatchQueue];
    hcSwatchQueue.clear();
    /** @type {Array<{host: Element, strip: Element, overflowing: boolean, atStart: boolean, atEnd: boolean, resetScroll: boolean}>} */
    const writes = [];
    for (const card of cards) {
      if (card.isConnected) writes.push(...card.hcReadSwatchState());
    }
    for (const w of writes) {
      if (w.overflowing !== w.host.hasAttribute('data-hc-overflowing')) {
        w.host.toggleAttribute('data-hc-overflowing', w.overflowing);
      }
      if (w.atStart !== w.host.hasAttribute('data-hc-at-start')) {
        w.host.toggleAttribute('data-hc-at-start', w.atStart);
      }
      if (w.atEnd !== w.host.hasAttribute('data-hc-at-end')) {
        w.host.toggleAttribute('data-hc-at-end', w.atEnd);
      }
      if (w.resetScroll) w.strip.scrollLeft = 0;
    }
    // Focus rescue runs after the writes: an arrow that just hid itself
    // would otherwise drop focus to <body>.
    for (const w of writes) {
      const active = document.activeElement;
      if (!(active instanceof HTMLElement)) break;
      if (!active.classList.contains('hc-sw-arrow') || !w.host.contains(active)) continue;
      if (getComputedStyle(active).display !== 'none') continue;
      const other = active.classList.contains('hc-sw-arrow--next')
        ? w.host.querySelector('.hc-sw-arrow--prev')
        : w.host.querySelector('.hc-sw-arrow--next');
      if (other instanceof HTMLElement && getComputedStyle(other).display !== 'none') other.focus();
    }
  });
}

export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    const link = this.refs.productCardLink;
    if (!link.getAttribute('href')) return '';

    const url = new URL(link.href);
    const variantOverride = this.#getProductPageVariantOverride();

    if (variantOverride?.action === 'set') {
      url.searchParams.set('variant', variantOverride.variantId);
    } else if (variantOverride?.action === 'remove') {
      url.searchParams.delete('variant');
    }

    return url.toString();
  }

  /**
   * Gets the user-selected variant ID that still needs to be synced from product-card state.
   * @returns {string | null} Variant ID or null.
   */
  getSelectedVariantId() {
    const variantOverride = this.#getProductPageVariantOverride();
    return variantOverride?.action === 'set' ? variantOverride.variantId : null;
  }

  /**
   * Gets the user-selected variant override for product-page URLs.
   * @returns {{ action: 'set', variantId: string } | { action: 'remove' } | null}
   */
  #getProductPageVariantOverride() {
    const link = this.refs.productCardLink;
    if (link.getAttribute('href')) {
      const url = new URL(link.href);
      if (url.searchParams.has('variant')) return null;
    }

    const variantId = this.#getCheckedVariantId();
    if (variantId === undefined) return null;

    return variantId ? { action: 'set', variantId } : { action: 'remove' };
  }

  /** @returns {string | undefined} */
  #getCheckedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.prefetch(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.#handleQuickAdd();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Warm the hover layer early on cards in rails, whose featured image is eager.
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });

    this.#observeSwatchOverflow();
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
    this.#swatchResizeObserver?.disconnect();
    this.#swatchResizeObserver = null;
    hcSetHealRegistration(this, false);
    this.#swatchMutationObserver?.disconnect();
    this.#swatchMutationObserver = null;
    for (const s of this.#trackedStrips) {
      s.removeEventListener('scroll', this.#syncSwatches);
      s.removeEventListener('scrollend', this.#syncSwatches);
    }
    this.#trackedStrips.clear();
    document.removeEventListener('shopify:section:load', this.#syncSwatches);
    window.removeEventListener('pageshow', this.#syncSwatches);
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * HC custom - Horizon uses cross-document view transitions with bfcache, so
   * Back restores the page EXACTLY as it was left: a card can come back mid
   * swatch preview with no pointerleave ever coming. Snap the gallery back to
   * its canonical image (the selected variant's, else the initial slide). The
   * hover image itself is a CSS layer inside the slide and needs no help.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;
    this.resetVariant.cancel();
    this.#resetVariant();
  };

  /**
   * HC custom - theme editor only (the event never fires on the storefront).
   * Editor re-renders replace section children under persistent custom
   * elements, which can leave a card's gallery holding state for slides that
   * were just swapped out - the transient wrong-image-at-render class of bug.
   * Snap back to the canonical image, same as the bfcache restore.
   */
  #handleSectionLoad = () => {
    this.resetVariant.cancel();
    this.#resetVariant();
  };

  /* ── HC swatch carousel state ──────────────────────────────────────────
     Dawn-style: state comes from the scroller's OWN numbers (scrollWidth /
     clientWidth / scrollLeft), which are always current at read time - never
     from element rects, which capture whatever half-laid-out geometry existed
     the instant a callback happened to fire. scroll-padding-inline in the CSS
     parks the snap rest at scrollLeft 0, so plain thresholds are exact.

     Triggers: strip scroll + scrollend, ResizeObserver (card + strip),
     MutationObserver (leading sync in the same frame as an editor morph plus
     a trailing settle), section:load, pageshow - and a 900ms self-heal tick,
     because every observer above can be parked or missed (hidden documents,
     editor iframes, bfcache) and a missed trigger must cost one second, not
     a broken carousel until reload. The tick reads three cached numbers and
     toggles nothing unless state actually changed. */

  /** @type {ResizeObserver | null} */
  #swatchResizeObserver = null;

  /** @type {MutationObserver | null} */
  #swatchMutationObserver = null;

  /** @type {Set<Element>} */
  #trackedStrips = new Set();

  #swatchSyncQueued = false;

  #syncSwatches = () => {
    if (!this.isConnected) return;
    hcScheduleSwatchSync(this);
  };

  /**
   * READ-ONLY measurement pass. Never writes to the DOM (attribute writes
   * happen in the batched flush), so a page full of cards costs one layout.
   * @returns {Array<{host: Element, strip: Element, overflowing: boolean, atStart: boolean, atEnd: boolean, resetScroll: boolean}>}
   */
  hcReadSwatchState() {
    for (const s of this.#trackedStrips) {
      if (!s.isConnected) {
        s.removeEventListener('scroll', this.#syncSwatches);
        s.removeEventListener('scrollend', this.#syncSwatches);
        this.#swatchResizeObserver?.unobserve(s);
        this.#trackedStrips.delete(s);
      }
    }

    const out = [];
    for (const strip of this.querySelectorAll('.hc-sw-mode-scroll .swatches-list')) {
      const host = strip.closest('product-swatches');
      if (!host) continue;

      // Track the LIVE strip: section morphs replace nodes wholesale.
      if (!this.#trackedStrips.has(strip)) {
        strip.addEventListener('scroll', this.#syncSwatches, { passive: true });
        strip.addEventListener('scrollend', this.#syncSwatches, { passive: true });
        this.#swatchResizeObserver?.observe(strip);
        this.#trackedStrips.add(strip);
      }

      const range = strip.scrollWidth - strip.clientWidth;
      // Must exceed the edge deadband below, or a strip overflowing by a few
      // px at fractional zoom grows an arrow and a ~56px fade to reveal them.
      const overflowing = range > 4;
      // |scrollLeft|: RTL scrolls negative; distance from rest is what matters.
      const pos = Math.abs(strip.scrollLeft);
      out.push({
        host,
        strip,
        overflowing,
        atStart: !overflowing || pos <= 2,
        atEnd: !overflowing || pos >= range - 2,
        // A strip that no longer overflows but is still scrolled leaves its
        // swatches pushed off to one side with a band of empty card beside
        // them. Reachable whenever the content shrinks under a parked scroll
        // position - a re-render, a variant change, a resize.
        resetScroll: !overflowing && pos > 2,
      });
    }
    return out;
  }

  /** Stamped by every pointer-driven variant path; the gallery heal stands
   *  down near recent activity so it can never fight a live interaction. */
  hcLastPointerTs = 0;

  /**
   * HC custom - gallery position heal, same philosophy as the swatch heal:
   * a gallery whose LOGICAL slide and its pixels disagree (a swatch preview
   * whose reset never came because the pointerleave was eaten, a browser
   * re-applying a saved scrollLeft on Back or reload) must cost one tick,
   * not a wrong image until the next hover. The hover image itself is a CSS
   * layer inside the slide (card-gallery.liquid), so hover creates no state
   * for this to heal - the pair-slide orphans this once chased are gone.
   *
   * NEVER write scroller.scrollLeft here. A raw write moves the pixels but
   * leaves slideshow.current behind, so the slideshow scrolls straight back
   * and the next tick writes again - a visible tug of war, which is exactly
   * what a raw write once shipped. #resetVariant() goes through select(),
   * which moves the logical index AND the scroll together. Gates: never
   * while :hover, never within 1.5s of pointer activity, fine-pointer
   * devices only (registration ensures that; matches() is belt and braces).
   */
  hcHealGallery() {
    if (this.matches(':hover')) return;
    if (Date.now() - this.hcLastPointerTs < 1500) return;
    const slideshow = this.refs.slideshow;
    if (!slideshow) return;
    const scroller = slideshow.refs.scroller;
    const currentSlide = slideshow.slides?.[slideshow.current];
    if (!scroller || !currentSlide) return;
    const base = slideshow.slides?.[0]?.offsetLeft || 0;
    if (Math.abs(scroller.scrollLeft - (currentSlide.offsetLeft - base)) <= 4) return;

    this.resetVariant.cancel();
    this.#resetVariant();
  }

  /*
   * The self-heal tick only exists to cover triggers the environment can
   * eat, and only a scroll strip has state to heal. swatch_overflow
   * defaults to "count", so on a default store this keeps a 50-card
   * collection at zero timers instead of 50.
   */
  #updateSwatchHealTimer() {
    // Gallery heal rides the same registration: fine-pointer devices only
    // (touch swipes park galleries legitimately; a mouse cannot).
    const needsGallery = !!this.querySelector('slideshow-component')
      && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    hcSetHealRegistration(this, needsGallery || !!this.querySelector('.hc-sw-mode-scroll .swatches-list'));
  }

  #observeSwatchOverflow() {
    // Unconditional: a swatches block can be ADDED in the editor to a card
    // that connected without one, and editor morphs never reconnect this
    // element - so the watchers must already be running. Every path funnels
    // into #syncSwatches, a cheap no-op without a scroll strip.
    this.#swatchResizeObserver = new ResizeObserver(this.#syncSwatches);
    this.#swatchResizeObserver.observe(this);
    this.#syncSwatches();
    document.addEventListener('shopify:section:load', this.#syncSwatches);
    window.addEventListener('pageshow', this.#syncSwatches);
    this.#updateSwatchHealTimer();
    this.#swatchMutationObserver = new MutationObserver(() => {
      // Leading edge: a morphed-in strip arrives with no state attributes;
      // restoring them in the same frame is what prevents the flash. The
      // observer watches childList only, so re-toggling attributes here
      // cannot self-loop.
      this.#updateSwatchHealTimer();
      this.#syncSwatches();
      if (this.#swatchSyncQueued) return;
      this.#swatchSyncQueued = true;
      // Trailing settle after layout (images sizing the strip). setTimeout,
      // not rAF: rAF parks in hidden/background documents.
      setTimeout(() => {
        this.#swatchSyncQueued = false;
        this.#syncSwatches();
      }, 60);
    });
    this.#swatchMutationObserver.observe(this, { childList: true, subtree: true });
  }

  /**
   * Warms the current slide's hover layer so the CSS reveal has pixels.
   *
   * HC: never before the image actually ON SCREEN has loaded. Taking the
   * layer out of the lazy queue puts it in a straight race with the featured
   * image, and in any section below the eager cutoff (section.index < 3) the
   * featured image is still lazy - the SECOND image could arrive first.
   * Waiting costs nothing: the shopper has to reach the card before they can
   * hover it, and the layer warms the moment the first image is done.
   */
  #preloadNextPreviewImage() {
    const slideshow = this.refs.slideshow;
    const currentSlide = slideshow?.slides?.[slideshow?.current];
    const pairImage = currentSlide?.querySelector('.product-media__hover img[loading="lazy"]');
    if (!pairImage) return;

    const warm = () => {
      // sizes="auto, ..." is only valid while lazy - pin to the card width first.
      this.#pinSizes(pairImage);
      pairImage.removeAttribute('loading');
    };
    const currentImage = currentSlide?.querySelector('img');
    if (currentImage && !currentImage.complete) {
      currentImage.addEventListener('load', warm, { once: true });
      // A featured image that never loads must not strand the hover feature.
      currentImage.addEventListener('error', warm, { once: true });
      return;
    }

    warm();
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Update variant picker when variant:selected event fires
    const { optionValueId, variantId, connectedProductUrl } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    // Empty string removes ?variant=.
    if (typeof variantId === 'string') {
      this.applyVariantToLinks(variantId, typeof connectedProductUrl === 'string' ? connectedProductUrl : undefined);
    }

    // Wait for variant:update data via promise
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        // Update price, availability, and URL based on new variant
        this.updatePrice(html);
        this.#updateBadges(html);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(html, typeof variantId === 'string' ? variantId : undefined);
        this.refs.quickAdd?.prefetch(this.productPageUrl);

        // Arrow-keying between swatch radios selects each one, and this
        // re-render can replace the focused input - capture its identity and
        // restore focus so keyboard colour-browsing doesn't dump to <body>.
        const focusedOptionId =
          this.contains(document.activeElement) && document.activeElement instanceof HTMLInputElement
            ? document.activeElement.dataset.optionValueId
            : null;

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        if (focusedOptionId && !this.contains(document.activeElement)) {
          /** @type {HTMLElement | null} */ (
            this.querySelector(`input[data-option-value-id="${focusedOptionId}"]`)
          )?.focus({ preventScroll: true });
        }

        this.#updateVariantImages();

        // Remove attribute after re-rendering since a variant selection has been made
        this.removeAttribute('data-no-swatch-selected');

        // Force overflow list to reflow after variant update
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    // HC: a live card holds exactly ONE priceContainer, so the upstream [1]
    // index never matched and the price morph was dead code. Last-element
    // keeps working if a second container ever appears (upstream's intent).
    const priceContainers = this.querySelectorAll(`product-price [ref='priceContainer']`);
    const priceContainer = priceContainers[priceContainers.length - 1];
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * HC: the Sale badge is judged by the variant the card shows
   * (snippets/hc-product-badges), so it follows the swatch the way the price
   * does - the single-card render carries the badge holder; morph it in and
   * keep the gallery's badge-position flag in step. Sold-out cards are left
   * alone: that state is product-wide and never changes with a swatch.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #updateBadges(html) {
    const current = this.querySelector('.product-badges');
    const next = html.querySelector('.product-badges');
    if (!current || !next) return;
    morph(current, next);
    // morph() updates children only by default: carry the holder's own
    // hidden flag across, or a badge swapped in stays invisible.
    current.hidden = next.hidden;

    const gallery = this.querySelector('.card-gallery');
    if (!gallery || gallery.classList.contains('card-gallery--sold-out')) return;
    const position = [...next.classList].find((c) => c.startsWith('product-badges--'))?.slice('product-badges--'.length);
    [...gallery.classList].filter((c) => c.startsWith('card-gallery--badge-')).forEach((c) => gallery.classList.remove(c));
    if (position && !next.hidden) gallery.classList.add(`card-gallery--badge-${position}`);
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   * @param {string} [intendedVariantId] - User-intended variant.
   */
  #updateProductUrl(html, intendedVariantId) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const responseUrl = new URL(anchorElement.href);
      this.#updateLinks({
        pathname: responseUrl.pathname,
        variantId: intendedVariantId ?? responseUrl.searchParams.get('variant'),
      });
    }
  }

  /**
   * Applies the given variant ID to the variant param of every link in the card.
   * Public so the components, namely swatches component can call it.
   * @param {string | null} variantId - The variant ID to set, or null to remove it.
   * @param {string} [productUrl] - Product URL path to adopt.
   */
  applyVariantToLinks(variantId, productUrl) {
    let pathname;
    if (productUrl) {
      try {
        pathname = new URL(productUrl, window.location.origin).pathname;
      } catch (error) {
        console.warn('[product-card] Invalid product URL:', productUrl, error);
        return;
      }
    }

    this.#updateLinks({ pathname, variantId });
  }

  /**
   * Updates every link in the card (overlay, gallery, and title) in place, preserving
   * each link's existing query params so tracking params stay in sync.
   * Always sets (or removes) the `variant` param; optionally adopts a new pathname (for combined-listing child navigation).
   * @param {object} options
   * @param {string} [options.pathname] - New pathname to adopt (combined-listing child navigation). Omit to keep each link's current path.
   * @param {string | null} [options.variantId] - Variant ID to set, or null/undefined to remove it.
   */
  #updateLinks({ pathname, variantId }) {
    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    for (const linkEl of [productCardLink, cardGalleryLink, productTitleLink]) {
      if (!(linkEl instanceof HTMLAnchorElement) || !linkEl.href) continue;
      const url = new URL(linkEl.href);
      if (pathname) url.pathname = pathname;
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
      linkEl.href = url.toString();
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #isUnavailableVariantSelected(html) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (html.querySelectorAll('input:checked'));

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    this.hcLastPointerTs = Date.now();
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      // HC perf: the colourway's slides may not exist yet (only the on-show
      // colour is server-rendered) - build them before the swap machinery
      // runs, so the hide/reveal loop and select({id}) see them in refs.
      this.#ensureSlidesForVariant(selectedImageId);
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        const isSelected = slide.getAttribute('slide-id') === selectedImageId;
        slide.hidden = !isSelected;
        // HC: single-visible-slide galleries never construct a Scroller, so the
        // select() below is a no-op there - keep aria-hidden coherent ourselves
        // (nested carousels paint slides via aria-hidden, see slideshow-styles).
        slide.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
      }

      this.#preloadVariantImage(selectedImageId);
      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
      this.#preloadPairImage(selectedImageId);
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   * @param {PointerEvent} [event] - The pointer event.
   */
  previewVariant(id, event) {
    // HC: pointerenter fires on touch-down too - a thumb-scroll over the swatch
    // row would flip the card image and start high-priority fetches. Taps still
    // select through click -> change -> #updateVariantImages.
    if (event?.pointerType !== 'mouse') return;
    // Phantom enter (content shifted under a parked cursor) - not a hover.
    if (!hcPointerActuallyMoved(event)) return;

    this.hcLastPointerTs = Date.now();
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    // HC perf: only the on-show colour is server-rendered; hovering a swatch
    // is the prefetch moment - build the colourway's slides now so the
    // preloads below warm both images before any click.
    this.#ensureSlidesForVariant(id);
    this.#preloadVariantImage(id);
    slideshow.select({ id }, undefined, { animate: false });
    // HC custom: the colour's second image lives in a hidden slide whose <img> is
    // lazy, and hidden slides never intersect, so the first hover paid a cold
    // fetch. Start it now, while the user is still deciding to hover.
    this.#preloadPairImage(id);
  }

  /**
   * HC custom - scrolls the swatch strip by one visible width (scroll
   * overflow mode's desktop arrows; touch scrolls the strip natively).
   * @param {string} direction - 'prev' or 'next'.
   */
  scrollSwatches(direction, event) {
    // Scope to the block whose arrow was clicked - a card may carry several
    // swatches blocks, and a count-mode block ordered first must not capture
    // the arrows of the scroll-mode block behind it.
    const clicked =
      event?.target instanceof Element ? event.target.closest('product-swatches.hc-sw-mode-scroll') : null;
    const list = clicked
      ? clicked.querySelector('.swatches-list')
      : this.querySelector('.hc-sw-mode-scroll .swatches-list');
    if (!list) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = getComputedStyle(list).direction === 'rtl';

    /*
     * Step by a whole number of SWATCHES, not a blind fraction of the
     * scrollport. A narrow card can have a scrollable range smaller than the
     * step - a 234px strip with 107px of travel was being scrolled 211px - so
     * every tap slammed to the far end and the carousel had exactly two
     * positions. Whole-swatch steps also land the strip on a swatch edge
     * instead of mid-circle (there is deliberately no scroll-snap - see the
     * strip CSS - so the arrows provide that alignment themselves).
     */
    const items = [...list.children].filter((li) => li.getBoundingClientRect().width >= 1);
    if (!items.length) return;
    const first = items[0].getBoundingClientRect();
    const pitch = items.length > 1 ? Math.abs(items[1].getBoundingClientRect().left - first.left) : first.width;
    const perPage = Math.max(1, Math.floor(list.clientWidth / Math.max(1, pitch)));
    // Keep one swatch of context when a full page fits more than one.
    const advance = Math.max(1, perPage - (perPage > 1 ? 1 : 0));
    /*
     * scrollTo an ABSOLUTE clamped target, never scrollBy. A relative scroll
     * issued while a smooth scroll is still animating re-bases on wherever
     * the animation currently is, so taps land short and the strip can stall
     * without ever reaching an edge - which is what made the prev arrow feel
     * endless. An absolute target always terminates at 0 or at the far end.
     */
    const range = Math.max(0, list.scrollWidth - list.clientWidth);
    const current = Math.abs(list.scrollLeft);
    const step = advance * pitch;
    const target = Math.min(range, Math.max(0, current + (direction === 'prev' ? -step : step)));

    list.scrollTo({ left: rtl ? -target : target, behavior: reduce ? 'auto' : 'smooth' });
    this.#syncSwatches();
    // Smooth scrolling finishes asynchronously and scrollend is not reliable
    // on every engine; settle the final state explicitly.
    setTimeout(this.#syncSwatches, 400);
  }


  /*
   * ── HC on-demand colourway slides ─────────────────────────────────────
   * Other colourways' slides are no longer server-rendered: card-gallery
   * skips every variant image that isn't the one on show, plus their hover
   * pairs (a 13-colour card used to ship 26 slides; collection documents
   * measured ~2.4MB, and document streaming time WAS the LCP bottleneck).
   * Their data ships in a compact JSON manifest per card instead
   * (script[data-hc-card-media]), and this cluster builds a DOM-equivalent
   * slide the moment a colourway is activated: swatch pointerenter
   * (previewVariant - which doubles as the prefetch, because the preloads
   * right after it strip loading=lazy on both fresh slides), swatch click
   * (#updateVariantImages), and reset (#resetVariant). Created slides carry
   * exactly the attributes the server would have written - ref="slides[]",
   * slide-id, variant-image, hidden, aria-hidden="true", plus the colour's
   * hover layer - so select-by-id, reveal, and reset-to-variant behave
   * identically to server-rendered ones. A section morph (collection
   * filtering) removes created slides again - they aren't in the server
   * HTML - and refreshes the manifest text; the next interaction simply
   * re-creates them from the fresh manifest.
   */

  /** @type {{ text: string | null, data: HcCardMediaManifest | null } | null} */
  #cardMediaCache = null;

  /**
   * Parses the per-card media manifest.
   * Cached against the script's text so a morph-refreshed manifest reparses.
   * @returns {HcCardMediaManifest | null}
   */
  #getCardMediaData() {
    const el = this.querySelector('script[data-hc-card-media]');
    if (!el) return null;
    const text = el.textContent;
    if (this.#cardMediaCache && this.#cardMediaCache.text === text) return this.#cardMediaCache.data;
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (error) {
      console.warn('[product-card] Invalid media manifest:', error);
    }
    this.#cardMediaCache = { text, data };
    return data;
  }

  /**
   * Ensures a colourway's slide pair exists in the gallery, building the
   * missing ones from the manifest. No-op when the slides are already in the
   * DOM (the on-show colour, or a colourway ensured earlier).
   * @param {string | number | undefined} mediaId - The variant image's media id.
   */
  #ensureSlidesForVariant(mediaId) {
    if (mediaId == null) return;
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const id = String(mediaId);
    // A slide carries its own hover layer, so its presence settles the colour.
    if (slideshow.querySelector(`slideshow-slide[slide-id="${CSS.escape(id)}"]`)) return;

    const data = this.#getCardMediaData();
    const entry = data?.media?.[id];
    const scroller = slideshow.refs.scroller;
    // The first slide is always server-rendered; it donates the class/style
    // conventions Liquid wrote for this specific card (media class suffixes,
    // grid rows, media-fit, the imagesToTransition ref).
    const template = scroller?.querySelector('slideshow-slide');
    if (!scroller || !template || !entry) return;

    // The colour's second shot rides inside the slide as its hover layer
    // (the markup product-media.liquid renders for server slides), looked up
    // through the manifest's pair id. After a featured-media promotion
    // (filtered collections) that media can already be a plain gallery
    // slide; as a layer it never needs a slide-id, so nothing collides.
    const pairId = entry[4] ? String(entry[4]) : '';
    const pairEntry = pairId ? data?.media?.[pairId] : null;
    scroller.append(this.#buildSlide(id, entry, data, template, pairEntry));

    // Refs update via MutationObserver at the microtask checkpoint - too late
    // for the select({id}) that follows synchronously. updatedCallback() is
    // the designed synchronous refresh (morph relies on it the same way).
    slideshow.updatedCallback();
  }

  /**
   * Builds a slideshow-slide DOM-equivalent to what card-gallery.liquid
   * renders (slideshow-slide > .product-media > img [+ .product-media__hover
   * > img]), hidden like every not-on-show slide, ready for the select({id})
   * reveal machinery.
   * @param {string} id - The media id.
   * @param {HcCardMediaEntry} entry - The manifest entry.
   * @param {HcCardMediaManifest | null} data - The parsed manifest (for the sizes attr).
   * @param {Element} template - A server-rendered slide to copy conventions from.
   * @param {HcCardMediaEntry | null | undefined} pairEntry - The colour's second shot, rendered as the hover layer.
   * @returns {HTMLElement}
   */
  #buildSlide(id, entry, data, template, pairEntry) {
    const width = Number(entry[1]) || 0;
    const height = Number(entry[2]) || 0;
    const mediaType = typeof entry[5] === 'string' && entry[5] ? entry[5] : 'image';

    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('slide-id', id);
    slide.setAttribute('aria-hidden', 'true');
    slide.setAttribute('hidden', '');
    slide.setAttribute('variant-image', '');
    slide.className = template.className.replace(
      /product-media-container--[a-z_-]+/,
      `product-media-container--${mediaType}`
    );
    // The timeline var only drives scroll-driven controls, which cards don't
    // render; the DOM position is a serviceable index.
    const index = template.parentElement ? template.parentElement.children.length : 0;
    const templateStyle = template.getAttribute('style') || '';
    slide.setAttribute('style', templateStyle.replace(/--slide-\d+/, `--slide-${index}`));

    const media = document.createElement('div');
    media.className = 'product-media';
    media.setAttribute('data-media-id', id);
    if (width && height) {
      media.style.setProperty('--ratio', String(Math.round((width / height) * 1000) / 1000));
    }

    const img = this.#buildMediaImage(entry, data);
    const transitionRef = template.querySelector('img')?.getAttribute('ref');
    if (transitionRef) img.setAttribute('ref', transitionRef);
    media.append(img);

    // Hover layer - the same markup product-media.liquid renders for the
    // server slides: decorative (aria-hidden, empty alt), lazy until warmed.
    if (pairEntry) {
      const hover = document.createElement('span');
      hover.className = 'product-media__hover';
      hover.setAttribute('aria-hidden', 'true');
      const hoverImg = this.#buildMediaImage(pairEntry, data);
      hoverImg.classList.add('product-media__hover-image');
      hoverImg.setAttribute('alt', '');
      hoverImg.setAttribute('draggable', 'false');
      hover.append(hoverImg);
      media.append(hover);
    }

    slide.append(media);
    return slide;
  }

  /**
   * Builds the <img> product-media.liquid would have rendered for a manifest
   * entry (srcset capped like card_widths, lazy, focal point).
   * @param {HcCardMediaEntry} entry - The manifest entry.
   * @param {HcCardMediaManifest | null} data - The parsed manifest (for the sizes attr).
   * @returns {HTMLImageElement}
   */
  #buildMediaImage(entry, data) {
    const url = String(entry[0] ?? '');
    const width = Number(entry[1]) || 0;
    const height = Number(entry[2]) || 0;
    const alt = typeof entry[3] === 'string' ? entry[3] : '';
    const focal = typeof entry[6] === 'string' ? entry[6] : '';

    const img = document.createElement('img');
    img.className = 'product-media__image';
    // Mirror the card_widths srcset cap from card-gallery.liquid, clamped to
    // the image's intrinsic width the way image_tag clamps.
    const candidates = [240, 352, 550, 832, 1200].filter((w) => w < width);
    if (width && width <= 1200) candidates.push(width);
    if (!candidates.length) candidates.push(1200);
    img.setAttribute('srcset', candidates.map((w) => `${this.#withImageWidth(url, w)} ${w}w`).join(', '));
    img.setAttribute('src', this.#withImageWidth(url, 3840));
    // image_tag stamps the REQUESTED width (3840) as the dimension hint even
    // past the intrinsic size; only the ratio matters, but match it exactly.
    img.setAttribute('width', '3840');
    if (width && height) img.setAttribute('height', String(Math.round(3840 / (width / height))));
    img.setAttribute('alt', alt);
    img.setAttribute('sizes', typeof data?.sizes === 'string' && data.sizes ? data.sizes : 'auto');
    img.setAttribute('loading', 'lazy');
    img.setAttribute('fetchpriority', 'auto');
    // Underscore name is deliberate: image_tag renders the kwarg verbatim and
    // zoom-dialog.js reads 'data_max_resolution'.
    img.setAttribute('data_max_resolution', this.#withImageWidth(url, 3840));
    if (focal) img.style.setProperty('--focal-point', focal);
    return img;
  }

  /**
   * Swaps the width param on a Shopify CDN image URL.
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #withImageWidth(url, width) {
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  }

  /**
   * HC custom - eagerly loads a variant image itself, at high priority.
   *
   * The variant's slide was hidden until this interaction, so its lazy <img>
   * never intersected and the fetch would only start at reveal - the card sat
   * blank until it landed. Warming the pair image alone made that worse: the
   * eager second image outranked the lazy first one, so hover had pixels while
   * the slide itself did not. The primary is fetched first and at high priority
   * so it always wins that race.
   * @param {string | number | undefined} mediaId - The variant image's media id.
   */
  #preloadVariantImage(mediaId) {
    if (mediaId == null) return;
    const image = this.refs.slideshow?.querySelector(
      `slideshow-slide[slide-id="${CSS.escape(String(mediaId))}"] img[loading="lazy"]`
    );
    if (!image) return;
    this.#pinSizes(image);
    image.removeAttribute('loading');
    image.fetchPriority = 'high';
  }

  /**
   * HC custom - `sizes="auto, ..."` is only valid on lazy images. Stripping
   * loading= makes selection fall through to the viewport-based entries and
   * fetch 832-1200w candidates for a ~300px card. Pin sizes to the card's
   * layout width first (the hidden slide's own img measures 0).
   * @param {HTMLImageElement} image
   */
  #pinSizes(image) {
    const width = this.getBoundingClientRect().width;
    if (width && image.sizes?.startsWith('auto')) image.sizes = `${Math.ceil(width)}px`;
  }

  /**
   * HC custom - eagerly loads the second image paired with a variant image
   * (the hover layer inside that colour's slide), so the first hover has
   * pixels ready instead of a cold lazy fetch.
   * @param {string | number | undefined} mediaId - The variant image's media id.
   */
  #preloadPairImage(mediaId) {
    if (mediaId == null) return;
    const image = this.refs.slideshow?.querySelector(
      `slideshow-slide[slide-id="${CSS.escape(String(mediaId))}"] .product-media__hover img[loading="lazy"]`
    );
    if (!image) return;
    this.#pinSizes(image);
    image.removeAttribute('loading');
  }

  /**
   * Gallery pointerenter. The hover itself is CSS: the second image is a
   * layer inside the current slide (card-gallery.liquid) revealed by :hover,
   * so there is no slide to move to and no scroll state to leave behind. The
   * jobs left here are warming that layer and keeping a swatch preview on
   * show while the pointer crosses from the swatch strip into the gallery.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    // Tell the heal a pointer interaction is live, so it stands down.
    this.hcLastPointerTs = Date.now();
    if (!this.refs.slideshow) return;

    // A phantom enter (content shifted under a parked cursor) may warm the
    // layer, but must not keep a swatch preview alive on the shopper's behalf.
    if (hcPointerActuallyMoved(event)) this.resetVariant.cancel();
    this.#preloadNextPreviewImage();
  }

  /**
   * Gallery pointerleave. Layer hover never moved the gallery; only a swatch
   * preview still in flight needs resetting (pointer went swatch -> gallery
   * -> away).
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    this.hcLastPointerTs = Date.now();
    if (this.variantPicker) this.#resetVariant();
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        // HC perf: covers restores where the selection outlives the slides
        // (bfcache, editor re-renders) - the slide may need rebuilding.
        this.#ensureSlidesForVariant(id);
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant. Touch guard mirrors previewVariant - pointerleave
   * fires when the finger lifts, and the debounced reset must not fight the
   * tap's own selection.
   */
  resetVariant = debounce(
    /** @param {PointerEvent} [event] */ (event) => {
      if (event?.pointerType !== 'mouse') return;
      this.#resetVariant();
    },
    100
  );
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Handles card swatch changes.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const hasAvailableVariant = clickedSwatch.dataset.hasAvailableVariant === 'true';
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Request the first available variant for this swatch.
    if (isSwatchInput && hasAvailableVariant && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      const optionValueId = clickedSwatch.dataset.optionValueId || '';
      const connectedProductUrl = clickedSwatch.dataset.connectedProductUrl || '';
      const requestUrl = this.buildRequestUrl(clickedSwatch, 'product-card', [optionValueId]);

      this.fetchUpdatedSection(requestUrl, {
        detail: { optionValueId, variantId: firstAvailableVariantId, connectedProductUrl },
      });
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
